import { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as EyeIcon } from 'src/assets/icons/eye.svg';
import { ReactComponent as HealthMonitorIcon } from 'src/assets/icons/healthMonitor.svg';
import LogoIcon from 'src/assets/icons/logoNavBar.png';
import { ReactComponent as PlayIcon } from 'src/assets/icons/play.svg';
import { ReactComponent as SettingsIcon } from 'src/assets/icons/settings.svg';
import { ReactComponent as UserIcon } from 'src/assets/icons/userIcon.svg';
import { ReactComponent as AdminIcon } from 'src/assets/icons/admin.svg';
import { UserSelectors } from 'src/reducers/user';
import { clearAll } from 'src/services/sessionStorage';
import { Size } from 'src/shared/text';
import { clearAuthToken } from 'src/utils/auth';
import { clearTermsAgreementStatus } from 'src/utils/termsAgreement';
import { Colors } from 'src/utils/color';

import {
    ChangePasswordModal,
    HealthMonitorModal,
    UploadVideoModal
} from './components';
import {
    BrainIcon,
    Content,
    ContentWrapper,
    EmptyView,
    IconButton,
    IconStyle,
    Icons,
    Item,
    LeftBarContainer,
    Menu,
    NavIconWrapper,
    PlayIconWrapper,
    RightNavWrapper,
    SettingMenu,
    Text,
    Topbar,
    Wrapper
} from './styles';
import { isJoeXingUser } from 'src/utils/utils';

type Props = {
    children?: React.ReactNode;
};

const menuOption = [
    'Account',
    'Notification',
    'Help center',
    'Change your password'
];

const DashboardWrapper: FunctionComponent<Props> = ({ children }: Props) => {
    const navigate = useNavigate();
    const modalRef = useRef<any>(null);
    const settingRef = useRef<any>(null);
    const healthModalRef = useRef<any>(null);
    const [menu, setMenu] = useState<boolean>(false);
    const [settingMenu, setSettingMenu] = useState<boolean>(false);
    const [csvLink] = useState<string>(
        'https://sample-videos.com/csv/Sample-Spreadsheet-10-rows.csv'
    );
    const { profile, isAllowedAdmin } = UserSelectors();
    const isJoeXingUserFlag = isJoeXingUser(profile);

    const onSettingItemClick = useCallback(
        (option: string) => {
            setSettingMenu(false);
            if (option === 'Account') {
                // Clear authentication and redirect to login
                clearAuthToken();
                clearAll();
                clearTermsAgreementStatus();
                navigate('/login');
            } else if (option === 'Change your password') {
                settingRef?.current?.show();
            }
        },
        [settingRef, navigate]
    );

    const tooltip = (label: string) => {
        return <Tooltip id="tooltip">{label}</Tooltip>;
    };

    const showSettingMenu = () => {
        setSettingMenu(true);
    };

    const hideSettingMenu = () => {
        setSettingMenu(false);
    };

    return (
        <>
            <Wrapper>
                <Topbar>
                    <NavIconWrapper />
                    <Icons 
                        src={LogoIcon} 
                        style={{
                            maxWidth: '217px',
                            maxHeight: '47px',
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain'
                        }}
                    />
                    <RightNavWrapper>
                        {profile?.first_name && profile?.last_name ? (
                            <>
                                <UserIcon style={IconStyle} />
                                <Text
                                    fontWeight="700"
                                    fontSize={Size.XXSmall}
                                    color={Colors.extra.white}
                                    lineHeight="25px"
                                    marginBottom={0}
                                    onClick={() => setMenu(!menu)}
                                >
                                    {profile.first_name} {profile.last_name}
                                </Text>
                                <Menu isOpen={menu}>
                                    <Item
                                        onClick={() => {
                                            clearAuthToken();
                                            clearAll();
                                            clearTermsAgreementStatus();
                                            setMenu(false);
                                            navigate('/');
                                        }}
                                    >
                                        <Text
                                            color={Colors.theme.primary}
                                            marginRight={0}
                                            fontFamily="Inter"
                                            fontSize={12}
                                            lineHeight={'15'}
                                            fontWeight="500"
                                            textAlign="center"
                                            width="100%"
                                            padding={3}
                                        >
                                            Log out
                                        </Text>
                                    </Item>
                                </Menu>
                            </>
                        ) : (
                            <Text
                                fontWeight="700"
                                fontSize={Size.XXSmall}
                                color={Colors.extra.white}
                                lineHeight="25px"
                                marginBottom={0}
                                onClick={() => navigate('/login')}
                                cursor="pointer"
                            >
                                Sign In
                            </Text>
                        )}
                    </RightNavWrapper>
                </Topbar>
                <ContentWrapper>
                    <LeftBarContainer>
                        {isJoeXingUserFlag && (
                        <>
                        <IconButton
                            onClick={() => healthModalRef.current.show()}
                        >
                            <HealthMonitorIcon />
                        </IconButton>
                        <IconButton onClick={() => modalRef.current.show()}>
                            <PlayIconWrapper>
                                <PlayIcon />
                            </PlayIconWrapper>
                        </IconButton>
                        <IconButton
                            href={csvLink}
                            onMouseEnter={hideSettingMenu}
                        >
                            <OverlayTrigger
                                placement="right"
                                overlay={tooltip('Vasculature Analytics')}
                            >
                                <EyeIcon />
                            </OverlayTrigger>
                        </IconButton>
                        </>
                        )}
                        <IconButton
                            onClick={() => navigate('/reasoning')}
                        >
                            <OverlayTrigger
                                placement="right"
                                overlay={tooltip('AI Reasoning')}
                            >
                                <BrainIcon>🧠</BrainIcon>
                            </OverlayTrigger>
                        </IconButton>
                        {isAllowedAdmin === true && (
                            <IconButton onClick={() => navigate('/admin/users')}>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={tooltip('Admin – Users & Health')}
                                >
                                    <AdminIcon style={{ ...IconStyle, width: 22, height: 22 }} />
                                </OverlayTrigger>
                            </IconButton>
                        )}
                        <IconButton 
                            onMouseEnter={showSettingMenu}
                            onMouseLeave={hideSettingMenu}
                        >
                            <SettingsIcon />
                        </IconButton>
                        <SettingMenu
                            onMouseEnter={showSettingMenu}
                            onMouseLeave={hideSettingMenu}
                            isOpen={settingMenu}
                        >
                            {menuOption.map((item: string, index: number) => {
                                return (
                                    <Item
                                        key={index}
                                        onClick={() => onSettingItemClick(item)}
                                    >
                                        <Text
                                            color="#ffffff"
                                            marginRight={0}
                                            fontFamily="Inter"
                                            fontSize={Size.XXSmall}
                                            fontWeight="600"
                                            textAlign="center"
                                            width="100%"
                                            padding={4}
                                        >
                                            {item}
                                        </Text>
                                    </Item>
                                );
                            })}
                        </SettingMenu>
                    </LeftBarContainer>
                    <Content>{children}</Content>
                </ContentWrapper>
            </Wrapper>
            <UploadVideoModal
                ref={modalRef}
                onHide={() => modalRef?.current?.hide()}
            />
            <HealthMonitorModal ref={healthModalRef} />
            <ChangePasswordModal
                ref={settingRef}
                onSave={(data) => {
                    settingRef?.current?.hide();
                }}
            />
        </>
    );
};

export default DashboardWrapper;
